import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Package } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

type Proveedor = { id: string; rfc: string; razon_social: string };

export function NuevaOCPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const reqID = sp.get('req') ?? '';

  const [proveedorID, setProveedorID] = useState('');
  const [fechaEntrega, setFechaEntrega] = useState('');
  const [condicionesPago, setCondicionesPago] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const { data: req } = useQuery<any>({
    queryKey: ['requisicion', reqID], enabled: !!reqID,
    queryFn: () => api(`/requisiciones/${reqID}`),
  });
  const { data: proveedores } = useQuery({
    queryKey: ['proveedores-pick'],
    queryFn: () => api<{ items: Proveedor[] }>('/proveedores?limit=500'),
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!proveedorID) { setErr('Selecciona un proveedor'); return; }
    setSubm(true); setErr(null);
    try {
      const oc = await api<{ id: string }>('/ordenes-compra', {
        method: 'POST',
        body: {
          requisicion_id: reqID || undefined,
          proveedor_id: proveedorID,
          fecha_entrega_pactada: fechaEntrega || undefined,
          condiciones_pago: condicionesPago || undefined,
        },
      });
      navigate(`/app/ordenes-compra/${oc.id}`);
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to={reqID ? `/app/requisiciones/${reqID}` : '/app/ordenes-compra'}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver
        </Link>
        <div className="mt-3 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Package size={20} />
          </div>
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Adquisiciones</p>
            <h1 className="font-display text-[24px] font-bold tracking-tight text-ink">Nueva orden de compra</h1>
            {req && <p className="text-[12px] text-ink-mute">Desde requisición <span className="num">{req.folio}</span> · {fmt.money(req.total_estimado)}</p>}
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}

        <Card>
          <CardHeader title="Datos de la orden" />
          <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="text-[13px] font-medium text-ink">Proveedor</label>
              <select required value={proveedorID} onChange={(e) => setProveedorID(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
                <option value="">(selecciona proveedor)</option>
                {(proveedores?.items ?? []).map(p => (
                  <option key={p.id} value={p.id}>{p.rfc} — {p.razon_social}</option>
                ))}
              </select>
            </div>
            <Input name="entrega" label="Fecha de entrega pactada" type="date"
              value={fechaEntrega} onChange={(e) => setFechaEntrega(e.target.value)} />
            <Input name="condiciones" label="Condiciones de pago" hint="Ej: 30 días DOC"
              value={condicionesPago} onChange={(e) => setCondicionesPago(e.target.value)} />
          </CardBody>
        </Card>

        {req && (
          <Card>
            <CardHeader title="Líneas heredadas de la requisición" subtitle="Se copiarán automáticamente al emitir la OC" />
            <CardBody className="p-0">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Descripción</th>
                    <th className="px-5 py-3 text-right">Cantidad</th>
                    <th className="px-5 py-3 text-right">P.U.</th>
                    <th className="px-5 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(req.lineas ?? []).map((l: any) => (
                    <tr key={l.id}>
                      <td className="px-5 py-3">{l.descripcion}</td>
                      <td className="num px-5 py-3 text-right">{fmt.num(l.cantidad)}</td>
                      <td className="num px-5 py-3 text-right">{fmt.money(l.precio_unitario_estimado)}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.money(l.total_estimado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}

        <div className="flex justify-end gap-2">
          <Link to={reqID ? `/app/requisiciones/${reqID}` : '/app/ordenes-compra'}>
            <Button variant="ghost" type="button">Cancelar</Button>
          </Link>
          <Button type="submit" loading={subm}>Emitir orden de compra</Button>
        </div>
      </form>
    </div>
  );
}
